import {
	type GraphQLSingularResponse,
	type CacheConfig,
	type RequestParameters,
	type Variables,
} from 'relay-runtime';
import delay from 'lodash/delay';

import { getAggEndpoint, getRequestBody } from '@atlassian/relay-endpoint';
import { getRelayExperimentalApis } from '@atlassian/relay-experimental-apis';
import { type AGGError, captureErrors } from '@atlassian/relay-errors';

import { createWrappedFetch } from '@confluence/graphql/entry-points/wrappedFetch.ts';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';
import { NoNetworkError } from '@confluence/network';

import { applyRelayQueryOverrides, shouldEnableQueryOverrides } from './applyRelayQueryOverrides.ts';
import { calculateExponentialBackoff } from './calculateExponentialBackoff.ts';

const wrappedFetch = createWrappedFetch(false);

const MAX_CONCURRENCY_FOR_THROTTLED = 8;
const NETWORK_ERROR_MAX_RETRIES = 2;
const NETWORK_ERROR_INITIAL_RETRY_DELAY_MS = 500;
const NETWORK_ERROR_MAX_RETRY_DELAY_MS = 5000;

const sendTrackEvent = (
	error: NoNetworkError,
	{
		queryName,
		priorRetries = 0,
	}: {
		queryName: string;
		priorRetries?: number;
	},
) => {
	void import(
		/* webpackChunkName: "loadable-confluenceanalytics-web-client" */ '@confluence/analytics-web-client'
	).then(({ getAnalyticsWebClient }) => {
		void getAnalyticsWebClient().then((analyticsWebClient) => {
			analyticsWebClient.sendOperationalEvent({
				source: 'ui',
				actionSubject: 'graphql',
				action: 'errored',
				attributes: {
					eventName: 'network.error.retry',
					queryName,
					priorRetries,
					message: error.message,
				},
			});
		});
	});
};

const requestPool: Promise<Response>[] = [];
const throttledFetch = (uri: string, options: RequestInit) => {
	if (requestPool.length >= MAX_CONCURRENCY_FOR_THROTTLED) {
		const queuedFetchFn = async () => {
			await requestPool.shift();
			return wrappedFetch(uri, options);
		};

		const queuedFetch: Promise<Response> = queuedFetchFn();
		requestPool.push(queuedFetch);
		return queuedFetch;
	}

	const nextFetch = wrappedFetch(uri, options);
	requestPool.push(nextFetch);
	return nextFetch;
};

const fetchFunctions = {
	throttledFetch,
	wrappedFetch,
};

export const __TEST_ONLY__ = {
	requestPool,
	fetchFunctions,
};

const shouldDisableBatchingForEnvironment = (): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}

	return (
		Boolean((window as any).Cypress) ||
		Boolean((window as any).__CRITERION__) ||
		Boolean((window as any).isPlaywright) ||
		Boolean((window as any).isContract) ||
		!StatsigConfigurations.isKillSwitchOn('confluence_spa_query_batching')
	);
};

const retryFetch = async (
	fetchFn: () => Promise<any>,
	{
		onErrorFn,
		shouldRetryFn,
		onRetryFn,
		maxRetries,
		initialDelayMs,
		maxDelayMs,
	}: {
		onErrorFn?: (error: Error, attempt: number) => void;
		shouldRetryFn?: (error: Error, attempt: number) => boolean;
		onRetryFn?: (error: Error, attempt: number) => void;
		maxRetries: number;
		initialDelayMs: number;
		maxDelayMs: number;
	},
): Promise<any> => {
	for (let attempt = 1; attempt <= maxRetries; attempt++) {
		try {
			return await fetchFn();
		} catch (error) {
			if (!shouldRetryFn?.(error, attempt) || attempt === maxRetries) {
				if (onErrorFn) {
					return onErrorFn(error as Error, attempt);
				} else {
					throw error;
				}
			}

			if (onRetryFn) {
				onRetryFn(error, attempt);
			}

			const exponentialDelay = calculateExponentialBackoff(attempt, initialDelayMs, maxDelayMs);

			// for testing, we want to avoid actual delays to prevent timeouts
			if (process.env.NODE_ENV !== 'testing') {
				await new Promise((resolve) => delay(resolve, exponentialDelay));
			}
		}
	}
};

interface BatchingConfig {
	/** Maximum number of requests to batch together */
	maxBatchSize: number;
	/** Maximum time to wait before sending a batch (in milliseconds) */
	batchInterval: number;
	/** Whether batching is enabled */
	enabled?: boolean;
}

export const DEFAULT_BATCHING_CONFIG: BatchingConfig = {
	maxBatchSize: 5,
	batchInterval: 50, // 50ms
};

interface BatchedRequest {
	request: RequestParameters;
	variables: Variables;
	cacheConfig: CacheConfig;
	resolve: (value: GraphQLSingularResponse) => void;
	reject: (error: Error) => void;
	id: string;
}

/**
 * BatchFetcher is a class that fetches multiple requests in a batch. Incoming requests are queued and processed in batches with a
 * max batch size of 5 and batch interval of 50ms. If batching is disabled, requests are processed individually using processSingleRequest.
 * Since request endpoints could be different, requests are grouped by endpoint and processed separately.
 *
 * Throttling behavior (mirrors Apollo's implementation):
 * - When metadata.throttle is set to true, requests bypass batching and use throttledFetch
 * - throttledFetch limits concurrent network requests to MAX_CONCURRENCY_FOR_THROTTLED (8)
 * - This is useful when a large volume of queries are triggered together, such as upon page load
 *
 * Usage: fetchQuery(environment, query, variables, { metadata: { throttle: true } })
 */
export class BatchFetcher {
	private batchingConfig: BatchingConfig;
	private requestQueue: BatchedRequest[];
	private batchTimeout: ReturnType<typeof setTimeout> | null = null;

	constructor(config: BatchingConfig = DEFAULT_BATCHING_CONFIG) {
		this.batchingConfig = config;
		this.requestQueue = [];
	}

	private generateRequestId(): string {
		return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
	}

	private shouldRetryFetch(error: Error, requests: RequestParameters[]): boolean {
		// Follow retry logic from confluence/next/packages/graphql/src/links/NetworkErrorRetryLink.ts
		return (
			requests.some((req) => req.operationKind !== 'mutation') && error instanceof NoNetworkError
		);
	}

	// Send track event when a retry is attempted
	private onRetryFn(error: Error, requests: RequestParameters[], attempt: number): void {
		if (error instanceof NoNetworkError) {
			requests.forEach((req) => {
				sendTrackEvent(error, {
					queryName: req.name,
					priorRetries: attempt,
				});
			});
		}
	}

	private async processSingleRequest(
		request: RequestParameters,
		variables: Variables,
		cacheConfig: CacheConfig,
		shouldThrottle?: boolean,
	): Promise<any> {
		const endpoint = getAggEndpoint(request, cacheConfig);
		const body = getRequestBody(request, variables);
		const fetchFn = shouldThrottle ? fetchFunctions.throttledFetch : fetchFunctions.wrappedFetch;

		return retryFetch(
			async () => {
				const response = await fetchFn(endpoint, {
					method: 'POST',
					credentials: 'same-origin',
					headers: {
						'content-type': 'application/json',
						'X-ExperimentalApi': getRelayExperimentalApis().join(','),
					},
					body,
				});

				let parsedResponse: GraphQLSingularResponse = await response.json();

				if (
					'errors' in parsedResponse &&
					parsedResponse.errors &&
					parsedResponse.errors.length > 0
				) {
					const errors = parsedResponse.errors as AGGError[];
					const requestId = parsedResponse.extensions?.gateway?.request_id;

					captureErrors(errors, {
						operationName: request.name,
						requestId,
					});
				}

				if (shouldEnableQueryOverrides()) {
					parsedResponse = applyRelayQueryOverrides(parsedResponse, request);
				}

				return parsedResponse;
			},
			{
				shouldRetryFn: (error) => this.shouldRetryFetch(error, [request]),
				onRetryFn: (error, attempt) => this.onRetryFn(error, [request], attempt),
				maxRetries: NETWORK_ERROR_MAX_RETRIES,
				initialDelayMs: NETWORK_ERROR_INITIAL_RETRY_DELAY_MS,
				maxDelayMs: NETWORK_ERROR_MAX_RETRY_DELAY_MS,
			},
		);
	}

	/**
	 * Groups requests by endpoint.
	 *
	 * @param requests - An array of BatchedRequest objects.
	 * @returns A record of requests grouped by endpoint.
	 */
	private getRequestsByEndpoint(requests: BatchedRequest[]): Record<string, BatchedRequest[]> {
		const requestsByEndpoint: Record<string, BatchedRequest[]> = {};

		for (const request of requests) {
			const endpoint = getAggEndpoint(request.request, request.cacheConfig);
			if (!requestsByEndpoint[endpoint]) {
				requestsByEndpoint[endpoint] = [];
			}
			requestsByEndpoint[endpoint].push(request);
		}

		return requestsByEndpoint;
	}

	/**
	 * Processes a batch of requests for a given endpoint.
	 *
	 * @param endpoint - The endpoint to process.
	 * @param endpointRequests - An array of BatchedRequest objects for the endpoint.
	 * @returns A promise that resolves when the requests are processed.
	 */
	private async processBatchForEndpoint(
		endpoint: string,
		endpointRequests: BatchedRequest[],
	): Promise<void> {
		if (endpointRequests.length === 1) {
			// Single request - use processSingleRequest
			const [request] = endpointRequests;
			try {
				const result = await this.processSingleRequest(
					request.request,
					request.variables,
					request.cacheConfig,
				);
				request.resolve(result);
			} catch (error) {
				request.reject(error as Error);
			}
		} else {
			// Multiple requests - batch them
			const batchBody = JSON.stringify(
				endpointRequests.map((req: BatchedRequest) => getRequestBody(req.request, req.variables)),
			);

			return retryFetch(
				async () => {
					const response = await wrappedFetch(endpoint, {
						method: 'POST',
						credentials: 'same-origin',
						headers: {
							'content-type': 'application/json',
							'X-ExperimentalApi': getRelayExperimentalApis().join(','),
						},
						body: batchBody,
					});

					const batchResponse: GraphQLSingularResponse[] = await response.json();

					endpointRequests.forEach((req: BatchedRequest, index: number) => {
						let batchedRes = batchResponse[index];
						if (
							batchedRes &&
							'errors' in batchedRes &&
							batchedRes.errors &&
							batchedRes.errors.length > 0
						) {
							const errors = batchedRes.errors as AGGError[];
							const requestId = batchedRes?.extensions?.gateway?.request_id;

							captureErrors(errors, {
								operationName: req.request.name,
								requestId,
							});
						}

						// Apply query overrides if enabled
						if (shouldEnableQueryOverrides()) {
							batchedRes = applyRelayQueryOverrides(batchedRes, req.request);
						}

						req.resolve(batchedRes); // always resolve, reject only in case of network level error
					});
				},
				{
					onErrorFn: (error) => {
						// If batch request fails, reject all requests for this endpoint
						endpointRequests.forEach((req: BatchedRequest) => {
							req.reject(error);
						});
					},
					shouldRetryFn: (error) =>
						this.shouldRetryFetch(
							error,
							endpointRequests.map((req) => req.request),
						),
					onRetryFn: (error, attempt) =>
						this.onRetryFn(
							error,
							endpointRequests.map((req) => req.request),
							attempt,
						),
					maxRetries: NETWORK_ERROR_MAX_RETRIES,
					initialDelayMs: NETWORK_ERROR_INITIAL_RETRY_DELAY_MS,
					maxDelayMs: NETWORK_ERROR_MAX_RETRY_DELAY_MS,
				},
			);
		}
	}

	/**
	 * Processes a batch of requests.
	 *
	 * @param requests - An array of BatchedRequest objects.
	 * @returns A promise that resolves when the requests are processed.
	 */
	private async processBatch(requests: BatchedRequest[]): Promise<void> {
		if (requests.length === 0) {
			return;
		}

		if (requests.length === 1) {
			const [request] = requests;
			try {
				const result = await this.processSingleRequest(
					request.request,
					request.variables,
					request.cacheConfig,
				);
				request.resolve(result);
			} catch (error) {
				request.reject(error as Error);
			}
			return;
		}

		const requestsByEndpoint = this.getRequestsByEndpoint(requests);

		// Process each endpoint group separately
		const promises = Object.entries(requestsByEndpoint).map(
			async ([endpoint, endpointRequests]) => {
				await this.processBatchForEndpoint(endpoint, endpointRequests);
			},
		);

		await Promise.allSettled(promises);
	}

	/**
	 * Schedules a batch of requests.
	 *
	 * @returns A promise that resolves when the requests are processed.
	 */
	private scheduleBatch(): void {
		if (this.batchTimeout) {
			clearTimeout(this.batchTimeout);
		}

		this.batchTimeout = setTimeout(() => {
			if (this.requestQueue.length > 0) {
				const batch = this.requestQueue.splice(0, this.batchingConfig.maxBatchSize);
				void this.processBatch(batch);

				// If there are more requests in the queue, schedule another batch
				if (this.requestQueue.length > 0) {
					this.scheduleBatch();
				}
			}
		}, this.batchingConfig.batchInterval);
	}

	/**
	 * Main method to fetch a request.
	 *
	 * @param request - The request to fetch.
	 * @param variables - The variables to fetch.
	 * @param cacheConfig - The cache config to fetch.
	 * @returns A promise that resolves when the request is processed.
	 */
	fetch(request: RequestParameters, variables: Variables, cacheConfig: CacheConfig): Promise<any> {
		// This bypass logic mirrors Apollo's split logic
		const shouldBypassBatch =
			!this.batchingConfig.enabled ||
			cacheConfig.metadata?.single ||
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			(!process.env.REACT_SSR &&
				(cacheConfig.metadata?.throttle === true || shouldDisableBatchingForEnvironment()));

		const shouldThrottle =
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			!process.env.REACT_SSR &&
			!shouldDisableBatchingForEnvironment() &&
			cacheConfig.metadata?.throttle === true;

		if (shouldBypassBatch) {
			return this.processSingleRequest(request, variables, cacheConfig, shouldThrottle);
		}

		return new Promise((resolve, reject) => {
			const batchedRequest: BatchedRequest = {
				request,
				variables,
				cacheConfig,
				resolve,
				reject,
				id: this.generateRequestId(),
			};

			this.requestQueue.push(batchedRequest);

			if (this.requestQueue.length >= this.batchingConfig.maxBatchSize) {
				const batch = this.requestQueue.splice(0, this.batchingConfig.maxBatchSize);
				void this.processBatch(batch);
			} else {
				this.scheduleBatch();
			}
		});
	}

	public setBatchingConfig(config: Partial<BatchingConfig>): void {
		this.batchingConfig = { ...this.batchingConfig, ...config };
	}

	public getBatchingConfig(): BatchingConfig {
		return { ...this.batchingConfig };
	}

	public clearRequestQueue(): void {
		// Reject all pending requests
		this.requestQueue.forEach((req) => {
			req.reject(new Error('Request queue cleared'));
		});
		this.requestQueue = [];

		if (this.batchTimeout) {
			clearTimeout(this.batchTimeout);
			this.batchTimeout = null;
		}
	}
}
